import React, { useEffect } from "react";
import {
  Menu,
  Bell,
  User,
  ChevronDown,
  // Settings,
  // HelpCircle,
  LogOut,
} from "lucide-react";
import useOutsideClick from "./useOutsideClick";
import { useLocation, useNavigate } from "react-router-dom";
import { menuConfig, type SidebarItem } from "./menuConfig";

interface HeaderProps {
  user: any;
  notificationCount: number;
  userMenuOpen: boolean;
  onMenuToggle: () => void;
  onUserMenuToggle: () => void;
  setUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onLogout: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  user,
  notificationCount,
  userMenuOpen,
  onMenuToggle,
  onUserMenuToggle,
  setUserMenuOpen,
  onLogout,
}) => {
  const ref = useOutsideClick<HTMLDivElement>(() => setUserMenuOpen(false));
  const navigate = useNavigate();
  const location = useLocation().pathname;

  const toTitleCase = (str: string) =>
    str.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    const handleScroll = () => setUserMenuOpen(false);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [setUserMenuOpen]);

  const isRouteMatch = (pathname: string, path?: string) => {
    if (!path) return false;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return (
      pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`)
    );
  };

  const getBreadcrumb = (pathname: string, role?: string): string[] => {
    const items: SidebarItem[] = menuConfig[role || "Individual"] || [];

    for (const item of items) {
      const activeChild = item.children?.find((child) =>
        isRouteMatch(pathname, child.path),
      );
      if (activeChild) return [item.label, activeChild.label];

      if (isRouteMatch(pathname, item.path)) return [item.label];
    }

    const fallback = pathname.split("/").filter(Boolean).slice(-1)[0];
    return [fallback ? toTitleCase(fallback) : "Dashboard"];
  };

  const breadcrumb = getBreadcrumb(location, user?.role);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div
        ref={ref}
        className="flex items-center justify-between px-4 lg:px-6 py-2"
      >
        {/* Left side - Mobile menu button and breadcrumb */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Menu size={20} />
          </button>

          <div className="hidden sm:flex items-center gap-2 text-sm">
            {breadcrumb.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="text-gray-400">/</span>}
                <span className="text-gray-900 font-medium text-lg">
                  {item}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        {/* Right side - Notifications and User menu */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <div className="relative">
            <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative">
              <Bell size={20} className="text-gray-600" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notificationCount}
                </span>
              )}
            </button>
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={onUserMenuToggle}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium text-gray-900">
                  {user?.name}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.role === "Individual" ? "Public User" : user?.role}
                </div>
              </div>
              <ChevronDown
                size={16}
                className="text-gray-500 hidden sm:block"
              />
            </button>

            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100 sm:hidden">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.name}
                  </div>

                  <div className="text-xs text-gray-500">{user?.role}</div>
                </div>
                <button
                  onClick={() => {
                    navigate("/profile");
                    setUserMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <User size={16} />
                  Profile
                </button>
                {/* <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                  <Settings size={16} />
                  Account Settings
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                  <HelpCircle size={16} />
                  Help & Support
                </button> */}
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <button
                    onClick={onLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
export default Header;
